import { useConfig } from '../hooks/useConfig';

/**
 * 流消息队列控制引擎
 *
 * 用于控制流式接口返回数据的输出速度。
 * - 非演示模式：收到消息后立即回调，无延迟。
 * - 演示模式：消息进入内部队列，按配置的最小时间间隔逐条输出，
 *   确保课程演示等场景下用户能看清每一步流程。
 *
 * 可复用于运行场景任务、组织创建等所有流式接口场景。
 *
 * 使用方式：
 * ```ts
 * const queue = new StreamQueue<MyItem>((item) => {
 *   // 处理每条消息
 * });
 * // 收到流数据时
 * queue.enqueue(item);
 * // 流结束后确保队列排空
 * await queue.flush();
 * // 组件卸载时清理
 * queue.destroy();
 * ```
 */
export class StreamQueue<T> {
  /** 内部消息队列 */
  private queue: T[] = [];
  /** 每条消息的处理回调 */
  private onItem: (item: T) => void;
  /** 队列消费定时器 */
  private timer: number | null = null;
  /** 上一条消息输出的时间戳 */
  private lastEmitTime = 0;
  /** 是否开启演示模式 */
  private demoMode: boolean;
  /** 演示模式最小输出间隔（毫秒） */
  private interval: number;
  /** 是否已销毁 */
  private destroyed = false;
  /** flush 时的 resolve 回调列表 */
  private flushResolvers: (() => void)[] = [];

  constructor(onItem: (item: T) => void) {
    this.onItem = onItem;

    // 从全局配置读取演示模式设置
    const { globalConfig } = useConfig();
    const cfg = globalConfig as { demoMode?: boolean; demoStreamInterval?: number };
    this.demoMode = cfg.demoMode ?? false;
    this.interval = cfg.demoStreamInterval ?? 300;
  }

  /**
   * 将消息加入队列
   * - 非演示模式：立即回调
   * - 演示模式：加入队列，按间隔逐条输出
   */
  enqueue(item: T): void {
    if (this.destroyed) return;

    if (!this.demoMode) {
      // 非演示模式，直接输出
      this.onItem(item);
      return;
    }

    // 演示模式：入队并启动消费
    this.queue.push(item);
    this.scheduleNext();
  }

  /**
   * 批量入队
   */
  enqueueBatch(items: T[]): void {
    for (const item of items) {
      this.enqueue(item);
    }
  }

  /**
   * 等待队列中所有消息输出完毕
   * 在流结束后调用，确保所有消息都被处理
   */
  flush(): Promise<void> {
    if (!this.demoMode || this.queue.length === 0) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.flushResolvers.push(resolve);
    });
  }

  /**
   * 销毁队列，清理定时器和未消费的消息
   */
  destroy(): void {
    this.destroyed = true;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.queue = [];
    // 释放所有等待中的 flush
    for (const resolve of this.flushResolvers) {
      resolve();
    }
    this.flushResolvers = [];
  }

  /**
   * 调度下一条消息的输出
   */
  private scheduleNext(): void {
    // 已有定时器在运行或队列为空，不重复调度
    if (this.timer !== null || this.queue.length === 0 || this.destroyed) return;

    const now = Date.now();
    const elapsed = now - this.lastEmitTime;
    const delay = Math.max(0, this.interval - elapsed);

    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.consumeOne();
    }, delay);
  }

  /**
   * 消费队列中的一条消息并调度下一条
   */
  private consumeOne(): void {
    if (this.destroyed || this.queue.length === 0) {
      this.resolveFlushIfEmpty();
      return;
    }

    const item = this.queue.shift()!;
    this.lastEmitTime = Date.now();
    this.onItem(item);

    if (this.queue.length > 0) {
      // 继续调度下一条
      this.scheduleNext();
    } else {
      // 队列已空，通知 flush 等待者
      this.resolveFlushIfEmpty();
    }
  }

  /**
   * 队列为空时释放所有 flush 等待
   */
  private resolveFlushIfEmpty(): void {
    if (this.queue.length === 0 && this.flushResolvers.length > 0) {
      for (const resolve of this.flushResolvers) {
        resolve();
      }
      this.flushResolvers = [];
    }
  }
}
